import { useEffect, useState } from 'react'
import { Controller, FormProvider, useForm, useFormContext } from 'react-hook-form'
import toast from 'react-hot-toast'
import { yupResolver } from '@hookform/resolvers/yup'

import ArrowLeftOutlined from '@ant-design/icons/ArrowLeftOutlined'
import DeleteOutlined from '@ant-design/icons/DeleteOutlined'
import EditOutlinedIcon from '@ant-design/icons/EditOutlined'

import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Stack from '@mui/material/Stack'
import { keyframes } from '@mui/material/styles'
import TextField from '@mui/material/TextField'
import IconButton from '@components/_mui/IconButton'

import useDialog from '@shared/hooks/src/useDialog'
import { isUserAllowed, useMe } from '@shared/providers/src/MeProvider'
import { UserRole } from '@shared/utils'

import ConfirmationV2 from '@components/Dialog/ConfirmationV2'

import { useSOPNotification } from '../SOPsNotifications/SOPsNotifications.hooks'
import Acknowledge from './components/Acknowledge'
import DocumentActions from './components/DocumentActions'
import DocumentHeader from './components/DocumentHeader'
import Editor from './editor/Editor'
import { getInitialValues, toAPIFormat, validationSchema } from './SOPDocument.utils'

export default function SOPDocument({ data, isCreate = false, showBack = false, onSave, onDelete, onBack }) {
  const [mode, setMode] = useState(isCreate ? 'edit' : 'view')
  const editable = mode === 'edit'

  const me = useMe()
  const canEdit = isUserAllowed(me, [UserRole.Admin])

  const form = useForm({ mode: 'all', resolver: yupResolver(validationSchema), defaultValues: getInitialValues(data) })

  // Keep the form in sync when the underlying SOP changes (analogue of enableReinitialize).
  useEffect(() => {
    form.reset(getInitialValues(data))
  }, [data, form])

  const { data: notification } = useSOPNotification(data?.id)

  const { isSubmitting, isDirty } = form.formState

  const handleCancel = () => {
    form.reset(getInitialValues(data))
    if (isCreate) onBack?.()
    else setMode('view')
  }

  const handleSave = form.handleSubmit(async (values) => {
    await onSave(toAPIFormat(values))
    if (!isCreate) setMode('view')
  })

  const confirmDelete = useDialog({
    component: ConfirmationV2,
    props: ({ close }) => ({
      maxWidth: 'xs',
      variant: 'error',
      title: 'Delete SOP',
      description: 'Are you sure you want to delete the SOP?',
      rejectLabel: 'Cancel',
      confirmLabel: 'Delete',
      onReject: () => close(),
      onConfirm: () =>
        onDelete().then(() => {
          close()
          toast.success('SOP has been deleted')
        }),
    }),
  })

  return (
    <FormProvider {...form}>
      <Box sx={styles.scroll}>
        <Stack sx={styles.page}>
          <Stack direction="row" sx={styles.titleRow}>
            {showBack && (
              <IconButton color="secondary" onClick={onBack} sx={styles.back} data-testid="sop-back">
                <ArrowLeftOutlined />
              </IconButton>
            )}
            <Title editable={editable} isCreate={isCreate} disabled={isSubmitting} />
          </Stack>

          <Toolbar
            editable={editable}
            canEdit={canEdit}
            data={data}
            isSubmitting={isSubmitting}
            isDirty={isDirty}
            onEdit={() => setMode('edit')}
            onCancel={handleCancel}
            onSave={handleSave}
            onDelete={confirmDelete}
          />

          <DocumentHeader editable={editable} disabled={isSubmitting} />

          <Body editable={editable} data={data} control={form.control} />

          {!editable && <Acknowledge notification={notification} />}
        </Stack>
      </Box>
    </FormProvider>
  )
}

function Title({ editable, isCreate, disabled }) {
  const { control } = useFormContext()

  return (
    <Controller
      name="title"
      control={control}
      render={({ field, fieldState }) => (
        <TextField
          {...field}
          inputRef={field.ref}
          variant="standard"
          fullWidth
          multiline
          autoFocus={isCreate}
          placeholder="Untitled SOP"
          disabled={disabled}
          // Keep the title a single logical line — wrapping for long titles, but no manual newlines.
          onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
          error={fieldState.isTouched && Boolean(fieldState.error)}
          helperText={fieldState.isTouched ? fieldState.error?.message : ''}
          slotProps={{ input: { readOnly: !editable, disableUnderline: !editable, 'data-testid': 'input-title' } }}
          sx={styles.titleInput}
        />
      )}
    />
  )
}

// The action row under the title: contextual notify/copy/edit/delete in view mode, and
// cancel/save while editing. Only a saved SOP (`data?.id`) exposes notify/copy/delete.
function Toolbar({ editable, canEdit, data, isSubmitting, isDirty, onEdit, onCancel, onSave, onDelete }) {
  if (editable) {
    return (
      <Stack direction="row" sx={styles.toolbarEnd}>
        <Button variant="outlined" color="secondary" onClick={onCancel} disabled={isSubmitting} data-testid="sop-update-cancel">
          Cancel
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={onSave}
          loading={isSubmitting}
          disabled={!isDirty}
          data-testid="sop-update-submit"
        >
          Save
        </Button>
      </Stack>
    )
  }

  return (
    <Stack direction="row" sx={styles.toolbar}>
      {data?.id && <DocumentActions data={data} />}
      <Box sx={{ flex: 1 }} />
      {canEdit && (
        <Stack direction="row" spacing={1}>
          <Button color="secondary" onClick={onEdit} startIcon={<EditOutlinedIcon />} data-testid="sop-edit">
            Edit
          </Button>
          {data?.id && (
            <Button color="error" onClick={onDelete} startIcon={<DeleteOutlined />} data-testid="sop-delete">
              Delete
            </Button>
          )}
        </Stack>
      )}
    </Stack>
  )
}

// Every SOP — including legacy v1 (Quill HTML) — renders through the v2 tiptap editor. v1 content
// is parsed from HTML on load (see Editor.jsx parseContent + legacy.js); the first edit re-saves
// it as v2 JSON, so SOPs migrate gradually as they're touched.
function Body({ editable, data, control }) {
  const editorKey = `${data?.id ?? 'new'}-${editable ? 'edit' : 'view'}`

  return (
    <Controller
      name="content"
      control={control}
      render={({ field, fieldState }) => (
        <Editor
          key={editorKey}
          editable={editable}
          value={field.value}
          onChange={field.onChange}
          onBlur={field.onBlur}
          error={editable && fieldState.isTouched ? fieldState.error?.message : ''}
        />
      )}
    />
  )
}

const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

const styles = {
  scroll: {
    height: '100%',
    overflowY: 'auto',
  },
  page: {
    maxWidth: 'md',
    mx: 'auto',
    px: { xs: 2.5, md: 7 },
    py: { xs: 3, md: 6 },
    gap: 2.5,
    animation: `${fadeInUp} 280ms ease`,
  },
  titleRow: {
    alignItems: 'center',
    gap: 1,
  },
  back: {
    flexShrink: 0,
  },
  titleInput: (theme) => ({
    position: 'relative',
    '& .MuiInputBase-input': {
      ...theme.typography.h2,
      textTransform: 'none',
      padding: 0,
      height: 'auto',
    },
    // Helper/error sits out of flow in the reserved bottom space, so a title error never
    // shifts the toolbar below it.
    '& .MuiFormHelperText-root': {
      position: 'absolute',
      top: '100%',
      margin: 0,
    },
  }),
  toolbar: {
    alignItems: 'center',
    flexWrap: 'wrap',
    justifyContent: 'space-between',
    gap: 1,
  },
  toolbarEnd: {
    justifyContent: 'flex-end',
    flexWrap: 'wrap',
    gap: 1.5,
  },
}
